// src/features/pages/admin/AdministrationPage.tsx
// Phase 3 (Model A): company-admin hub inside main-app. Cards are gated by the
// canonical RBAC permissions, so each admin sees only what they can manage.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Users, Building2, FolderTree, Calendar, Settings, ChevronRight, UploadCloud, Bot, Rocket, CalendarDays, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { useRbacRoles } from '@/rbac/useRbac';
import { hasPermission } from '@/rbac/rbac.generated';

interface AdminCard {
  titleKey: string;
  descKey: string;
  to: string;
  icon: React.ReactNode;
  perm: string;
}

const CARDS: AdminCard[] = [
  {
    titleKey: 'administration.cards.setup_title',
    descKey: 'administration.cards.setup_desc',
    to: 'setup',
    icon: <Rocket className="w-5 h-5" />,
    perm: 'department:write',
  },
  {
    titleKey: 'administration.cards.users_title',
    descKey: 'administration.cards.users_desc',
    to: 'users',
    icon: <Users className="w-5 h-5" />,
    perm: 'user:read:own-tenant',
  },
  {
    titleKey: 'administration.cards.roles_title',
    descKey: 'administration.cards.roles_desc',
    to: 'roles',
    icon: <ShieldCheck className="w-5 h-5" />,
    perm: 'user:role:assign',
  },
  {
    titleKey: 'administration.cards.departments_title',
    descKey: 'administration.cards.departments_desc',
    to: 'departments',
    icon: <FolderTree className="w-5 h-5" />,
    perm: 'department:write',
  },
  {
    titleKey: 'administration.cards.directory_title',
    descKey: 'administration.cards.directory_desc',
    to: '../employees',
    icon: <Building2 className="w-5 h-5" />,
    perm: 'employee:read',
  },
  {
    titleKey: 'administration.cards.import_title',
    descKey: 'administration.cards.import_desc',
    to: 'import',
    icon: <UploadCloud className="w-5 h-5" />,
    perm: 'employee:import',
  },
  {
    titleKey: 'administration.cards.leave_title',
    descKey: 'administration.cards.leave_desc',
    to: 'leave-policies',
    icon: <Calendar className="w-5 h-5" />,
    perm: 'leavepolicy:write',
  },
  {
    titleKey: 'administration.cards.holidays_title',
    descKey: 'administration.cards.holidays_desc',
    to: 'holidays',
    icon: <CalendarDays className="w-5 h-5" />,
    perm: 'leavepolicy:write',
  },
  {
    titleKey: 'administration.cards.ai_title',
    descKey: 'administration.cards.ai_desc',
    to: 'ai-assistant',
    icon: <Bot className="w-5 h-5" />,
    perm: 'ai-config:write',
  },
  {
    titleKey: 'administration.cards.compliance_title',
    descKey: 'administration.cards.compliance_desc',
    to: 'compliance',
    icon: <ShieldCheck className="w-5 h-5" />,
    perm: 'user:read:own-tenant',
  },
  {
    titleKey: 'administration.cards.settings_title',
    descKey: 'administration.cards.settings_desc',
    to: '../settings',
    icon: <Settings className="w-5 h-5" />,
    perm: 'tenant:update:own',
  },
];

export const AdministrationPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const roles = useRbacRoles();
  const cards = CARDS.filter((c) => hasPermission(roles, c.perm));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 text-charcoal">{t('administration.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('administration.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map((c) => (
          <Link key={c.to} to={c.to} className="group">
            <Card className="h-full transition-all hover:shadow-lg hover:border-orange-300 dark:hover:border-orange-500/40 border-orange-100 dark:border-orange-500/20">
              <CardContent className="p-5 flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-[10px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300">
                    {c.icon}
                  </span>
                  <span className="font-semibold text-foreground flex-1">{t(c.titleKey)}</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-orange-500 transition-colors" />
                </div>
                <p className="text-sm text-muted-foreground">{t(c.descKey)}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {cards.length === 0 && (
        <div className="py-16 text-center text-muted-foreground">
          {t('administration.empty')}
        </div>
      )}
    </div>
  );
};

export default AdministrationPage;
